import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Container, Overline, Reveal, PrimaryButton, SecondaryButton, TextLink, HairlineList } from "@/components/primitives";
import { useDocumentMeta, JsonLd, ORGANIZATION, SITE_NAME, SITE_ORIGIN } from "@/lib/meta";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    ORGANIZATION,
    {
      "@type": "WebSite",
      "@id": `${SITE_ORIGIN}/#website`,
      name: SITE_NAME,
      url: `${SITE_ORIGIN}/`,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      inLanguage: "en-GB",
    },
  ],
};

const loopSteps = [
  { n: "01", src: "ComplianceMinder", t: "Policy change identified", d: "A horizon scan or review flags a change your policies must reflect." },
  { n: "02", src: "ComplianceMinder", t: "Process change drafted", d: "The mandated process change is drafted from the approved policy update." },
  { n: "03", src: "Directors", t: "Directors approve", d: "The change is put to your directors for sign-off — nothing changes silently." },
  { n: "04", src: "CaseMinder", t: "Built into CaseMinder", d: "The approved change is built straight into how the firm actually works." },
];

export default function Home() {
  useDocumentMeta({
    fullTitle: "Compliance and case management software for law firms | Beyond the Box",
    description:
      "Compliance and case management software for owner-managed law firms, on the Microsoft 365 you already control. ComplianceMinder keeps your policies live and scans the horizon for SRA, Lexcel and Law Society changes. CaseMinder adds the matter workflow.",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      <JsonLd data={homeJsonLd} />
      {/* HERO */}
      <section className="relative border-b border-border" data-testid="home-hero">
        <div className="border-b border-border">
          <Container className="flex items-center justify-between py-3">
            <span className="overline text-muted-foreground">For owner-managed law firms</span>
            <span className="overline text-accent">Runs on your Microsoft 365</span>
          </Container>
        </div>
        <Container className="py-20 md:py-28">
          <Reveal>
            <Overline accent>The autonomy stack for law-firm owners</Overline>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-[3.25rem] font-extrabold leading-[0.9] tracking-[-0.04em] text-foreground sm:text-7xl md:text-[7rem]">
              Stop renting your firm from software vendors.
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-9 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              Own your compliance on the Microsoft 365 you already pay for. ComplianceMinder keeps
              your policies live, scans the horizon for SRA, Lexcel and Law Society changes, and
              never holds your data to ransom.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-11 flex flex-col gap-4 sm:flex-row sm:items-center">
              <PrimaryButton to="/book-a-walkthrough" testid="hero-cta-primary">
                Book a walkthrough
              </PrimaryButton>
              <SecondaryButton to="/playbook" testid="hero-cta-secondary" arrow>
                For IT teams → the M365 playbook
              </SecondaryButton>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* PROBLEM BAND */}
      <section className="border-b border-border py-20 md:py-28" data-testid="home-problem">
        <Container>
          <Reveal>
            <p className="max-w-5xl font-display text-3xl font-semibold leading-[1.05] tracking-[-0.02em] text-foreground md:text-5xl">
              Held to ransom by your software vendor.
              <span className="text-accent"> Paying compliance providers who don't show up.</span>
            </p>
            <p className="mt-6 max-w-2xl leading-relaxed text-muted-foreground">
              You are locked into a case-management system you tolerate — but your compliance is the
              open, anxious gap no one has really served. Start there, on infrastructure you already
              own.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* COMPLIANCEMINDER — start here */}
      <section className="border-b border-border py-24 md:py-32" data-testid="home-complianceminder">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline accent>Start here · ComplianceMinder</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Compliance that actually turns up.
              </h2>
              <div className="mt-8">
                <TextLink to="/complianceminder" testid="home-complianceminder-cta">
                  Explore ComplianceMinder
                </TextLink>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <p className="text-lg leading-relaxed text-foreground">
                Live, controlled policies. Firmwide risk assessment and a directors' panel.
                Twice-yearly horizon scans for SRA, Lexcel and Law Society updates.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Suggested policy changes, old versions retired to SharePoint with full version
                control, only the current version in force. Licence it standalone — or bolt it onto
                CaseMinder for a fuller firmwide risk picture.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* CASEMINDER — grow into it */}
      <section className="border-b border-border bg-secondary/40 py-24 md:py-32" data-testid="home-caseminder">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline accent>Grow into it · CaseMinder</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Your firm's operations, built around you.
              </h2>
              <div className="mt-8">
                <TextLink to="/caseminder" testid="home-caseminder-cta">
                  Explore CaseMinder
                </TextLink>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <p className="text-lg leading-relaxed text-foreground">
                A bespoke practice-management hub built around how your firm actually works.
                Compliant matter opening, a post hub, deep SharePoint and Outlook integration, and
                Outlook-grade webmail.
              </p>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                Built once, around you — you own it. No perpetual licence to use what you paid to
                build. Optional support package if you'd like us to look after it.
              </p>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* FEEDBACK LOOP — crown jewel */}
      <section id="how-it-works" className="border-b border-border py-24 md:py-32" data-testid="home-feedback-loop">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>The differentiator · How it works</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Two systems that teach each other.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Bolt ComplianceMinder onto CaseMinder and they feed each other. A policy update can
                mandate a process change — so the change is drafted, put to your directors for
                approval, and built straight into CaseMinder. Compliance that changes how the firm
                runs, not a folder of PDFs no one reads.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 border-l border-t border-border md:grid-cols-4">
            {loopSteps.map((s) => (
              <div
                key={s.n}
                className="flex h-full flex-col border-b border-r border-border p-6"
                data-testid={`home-loop-step-${s.n}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-sm font-bold tracking-widest text-accent">{s.n}</span>
                  <span className="overline text-muted-foreground">{s.src}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold tracking-[-0.02em] text-foreground">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* M365 BAND — trust/technical, for IT */}
      <section className="border-b border-border bg-secondary/40 py-24 md:py-32" data-testid="home-m365">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline accent>Built for your IT team</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Built on the Microsoft 365 you already control.
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                No black box. It runs in your own Microsoft 365 tenant — SharePoint, Outlook, your
                data, your estate. Your IT team can get under the bonnet, because they're the
                Microsoft experts and they're tired of systems they can't see into. We'll give them
                the playbook.
              </p>
              <div className="mt-8">
                <SecondaryButton to="/playbook" testid="home-m365-cta" arrow>
                  For IT teams → the M365 playbook
                </SecondaryButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.08}>
              <span className="overline text-muted-foreground">Your estate, your control</span>
              <HairlineList
                testid="home-m365-list"
                items={[
                  "Runs in your own M365 tenant",
                  "Your data never leaves your estate",
                  "Every Graph permission explained",
                  "Auditable by your own IT team",
                ]}
              />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* TRAINING BAND */}
      <section className="border-b border-border py-20 md:py-28" data-testid="home-training">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline accent>Training</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-5xl">
                Training your team will finish. Records that keep themselves.
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                Bespoke AML, process and policy, and practice-area training built around your firm.
                Each module ends in a short quiz, and each result lands on that person's training
                record.
              </p>
              <div className="mt-8">
                <SecondaryButton to="/training" testid="home-training-cta" arrow>
                  See training
                </SecondaryButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.08}>
              <span className="overline text-muted-foreground">Delivered and evidenced</span>
              <HairlineList
                testid="home-training-list"
                items={[
                  "Built on your own policies and processes",
                  "AML, policy and practice-area modules",
                  "Short quizzes, results recorded automatically",
                  "See who is done, who is overdue",
                ]}
              />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* PROOF — Bonsai Law as first client */}
      <section className="border-b border-border py-20 md:py-28" data-testid="home-proof">
        <Container>
          <Reveal>
            <Overline className="text-muted-foreground">Proof</Overline>
            <p className="mt-6 max-w-4xl font-display text-2xl font-semibold leading-[1.15] tracking-[-0.02em] text-foreground md:text-4xl">
              Built and proven inside a working law firm — Bonsai Law was the first firm to run it.
            </p>
            <p className="mt-6 max-w-2xl leading-relaxed text-muted-foreground">
              Not theory from a software vendor. The firm behind the build runs on it, and remains
              the escalation route for regulated legal advice.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* RESOURCES */}
      <section className="border-b border-border py-16 md:py-20" data-testid="home-resources">
        <Container>
          <Overline className="text-muted-foreground">Go deeper</Overline>
          <div className="mt-8 grid grid-cols-1 border-l border-t border-border sm:grid-cols-3">
            {[
              { name: "For IT teams", to: "/playbook", line: "The M365 playbook — request it for your IT team." },
              { name: "Insights", to: "/insights", line: "Notes on control and dependency." },
              { name: "FAQ", to: "/faq", line: "Straight answers, no theatre." },
            ].map((r) => (
              <Link
                key={r.to}
                to={r.to}
                data-testid={`home-resource-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex items-start justify-between border-b border-r border-border p-7 transition-colors hover:bg-secondary/50"
              >
                <div>
                  <div className="font-display text-xl font-bold tracking-[-0.02em] text-foreground transition-colors group-hover:text-accent">
                    {r.name}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.line}</p>
                </div>
                <ArrowUpRight
                  strokeWidth={2}
                  className="h-5 w-5 shrink-0 text-muted-foreground transition-all duration-200 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent"
                />
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-24 text-primary-foreground md:py-32" data-testid="home-final-cta">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Overline className="text-primary-foreground/60">The route out</Overline>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-[-0.03em] md:text-6xl">
              Own your compliance. Grow into your operations.
            </h2>
            <p className="mt-6 max-w-xl leading-relaxed text-primary-foreground/70">
              Start with ComplianceMinder on the Microsoft 365 you already control. Book a blunt,
              practical walkthrough built around your firm.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/book-a-walkthrough"
              data-testid="home-final-cta-btn"
              className="inline-flex items-center gap-3 bg-primary-foreground px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-primary transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              Book a walkthrough <span>→</span>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
